import { useState } from 'react';

import type { VectorSourceDescriptor } from '../../../data/model';
import type { LayerDefinition } from '../../../project/model';
import { categorisableFields, numericFields } from '../../../styles/classification';
import type { StyleClassificationMode } from '../../../styles/model';
import type { StyleChangeResult } from '../../state/appStore';
import { useAppStore } from '../../state/useAppStore';
import { SymbolControls } from './SymbolControls';
import { ThematicControls } from './ThematicControls';

interface StyleInspectorProps {
  readonly layer: LayerDefinition;
  readonly source: VectorSourceDescriptor;
}

export function StyleInspector({ layer, source }: StyleInspectorProps) {
  const [notice, setNotice] = useState<string | null>(null);
  const setClassificationMode = useAppStore((state) => state.setLayerClassificationMode);
  const classification = layer.style.classification;
  const scalarFields = categorisableFields(source.fields);
  const suitableNumericFields = numericFields(source.fields);

  const handleResult = (result: StyleChangeResult) => {
    setNotice(result.ok ? null : result.message);
  };

  return (
    <div className="inspector-content style-inspector" data-control-density="compact">
      <section className="inspector-section classification-section">
        <div className="style-field">
          <label htmlFor={`classification-${layer.id}`}>Classification</label>
          <select
            aria-describedby={notice ? `style-notice-${layer.id}` : undefined}
            id={`classification-${layer.id}`}
            onChange={(event) =>
              handleResult(
                setClassificationMode(layer.id, event.target.value as StyleClassificationMode),
              )
            }
            value={classification.mode}
          >
            <option value="single">Single symbol</option>
            <option disabled={scalarFields.length === 0} value="categorised">
              Categorised
            </option>
            <option disabled={suitableNumericFields.length === 0} value="graduated">
              Graduated
            </option>
          </select>
        </div>
        {scalarFields.length === 0 || suitableNumericFields.length === 0 ? (
          <p className="style-help">
            {scalarFields.length === 0
              ? 'No scalar attribute fields are available for thematic styling.'
              : 'Graduated styling needs a numeric field.'}
          </p>
        ) : null}
        {notice ? (
          <p className="style-notice" id={`style-notice-${layer.id}`} role="status">
            {notice}
          </p>
        ) : null}
      </section>

      {classification.mode === 'single' ? null : (
        <ThematicControls
          classification={classification}
          layer={layer}
          onResult={handleResult}
          source={source}
        />
      )}
      <SymbolControls layer={layer} onResult={handleResult} source={source} />
    </div>
  );
}
